import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActions,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { AddCircle as AddIcon, Groups as GroupsIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { Organization } from '../../types/Organization';
import { getUserOrganizations } from '../../services/organizationService';

const OrganizationsList: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const orgs = await getUserOrganizations(currentUser.uid);
        setOrganizations(orgs);
      } catch (err) {
        console.error('Error fetching organizations:', err);
        setError('Failed to load organizations. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [currentUser]);

  const handleCreateOrg = () => {
    navigate('/organizations/create');
  };

  const renderOrganizationCard = (org: Organization) => {
    // Determine if the user is the creator
    const isCreator = org.creatorId === currentUser?.uid;

    // Get user's role in the organization
    const userMember = org.members.find(m => m.userId === currentUser?.uid);
    const userRole = userMember?.role || 'member';

    return (
      <Card key={org.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {org.coverPhotoUrl ? (
          <CardMedia
            component="img"
            height="140"
            image={org.coverPhotoUrl}
            alt={org.name}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <Box 
            sx={{ 
              height: 140, 
              backgroundColor: 'primary.light', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}
          >
            <BusinessIcon sx={{ fontSize: 60, color: 'white' }} />
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
          <Avatar 
            src={org.logoUrl} 
            alt={org.name} 
            sx={{ 
              width: 56, 
              height: 56, 
              mr: 2,
              border: '2px solid white',
              boxShadow: 1,
              bgcolor: 'primary.main'
            }}
          >
            {org.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h6" component="h2" noWrap>
              {org.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <GroupsIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {org.members.length} member{org.members.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <CardContent sx={{ pt: 0, flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxHeight: 60, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {org.description}
          </Typography>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Chip 
              size="small" 
              label={isCreator ? 'Owner' : userRole} 
              color={isCreator ? 'primary' : 'default'} 
              variant={isCreator ? 'filled' : 'outlined'} 
            />
            {!org.isPublic && <Chip size="small" label="Private" />}
          </Box>
        </CardContent>
        
        <Divider />
        
        <CardActions>
          <Button 
            size="small" 
            component={Link} 
            to={`/organizations/${org.id}`}
            sx={{ flexGrow: 1 }}
          >
            View Details
          </Button>
          <Button 
            size="small" 
            variant="contained" 
            component={Link} 
            to={`/organizations/${org.id}/dashboard`}
          >
            Dashboard
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Your Organizations
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateOrg}
        >
          Create Organization
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : organizations.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            You don't have any organizations yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Create an organization to manage events, invite members, and build your community.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateOrg}
            sx={{ mt: 2 }}
          >
            Create Your First Organization
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {organizations.map((org) => (
            <Grid item key={org.id} xs={12} sm={6} md={4}>
              {renderOrganizationCard(org)}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default OrganizationsList; 